/**
 * Design token → CSS value maps.
 *
 * All form styling is expressed as tokens in FormSettings. The renderer
 * resolves them here — never read raw CSS values from the schema.
 */
import type {
  BorderColorToken,
  FontSizeToken,
  FontWeightToken,
  LineHeightToken,
  SpacingToken,
  BackgroundToken,
} from "@livingsyncs/types";

export const FONT_SIZE: Record<FontSizeToken, string> = {
  sm: "14px",
  md: "18px",
  lg: "22px",
  xl: "28px",
  "2xl": "34px",
};

export const FONT_WEIGHT: Record<FontWeightToken, number> = {
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
};

export const LINE_HEIGHT: Record<LineHeightToken, number> = {
  tight: 1.1,
  normal: 1.3,
  relaxed: 1.6,
};

export const BORDER_COLOR: Record<BorderColorToken, string> = {
  neutral: "#E2E8F0",
  muted: "#CBD5E1",
  primary: "#2563EB",
  danger: "#DC2626",
};

export const BORDER_WIDTH: Record<"sm" | "md" | "lg", string> = {
  sm: "1px",
  md: "2px",
  lg: "3px",
};

export const SPACING: Record<SpacingToken, string> = {
  sm: "8px",
  md: "16px",
  lg: "24px",
  xl: "32px",
};

export const BACKGROUND: Record<BackgroundToken, string> = {
  white: "#FFFFFF",
  light: "#F8FAFC",
  muted: "#F1F5F9",
  primary: "#EFF6FF",
  brand: "#E8F2EE",
};

/** Logo width px by token */
export const LOGO_WIDTH: Record<SpacingToken, number> = {
  sm: 40,
  md: 80,
  lg: 120,
  xl: 160,
};

/** Logo height px by token */
export const LOGO_HEIGHT: Record<SpacingToken, number> = {
  sm: 24,
  md: 40,
  lg: 56,
  xl: 72,
};

// ── Interpolation ────────────────────────────────────────────────────────────

/**
 * Replace {{variable}} placeholders in a string using values from `context`.
 * Unknown keys are left as-is (not replaced) rather than stripped.
 */
export function interpolate(
  template: string,
  context: Record<string, string | undefined>,
): string {
  return template.replace(/\{\{(\w+)\}\}/g, (match, key: string) => {
    return context[key] ?? match;
  });
}

/**
 * Build an interpolation context from form data + static metadata.
 * Renderer passes this when rendering a title section.
 */
export function buildInterpolationContext(
  data: Record<string, unknown>,
  staticContext?: {
    resident_name?: string;
    facility_name?: string;
    employee_name?: string;
    submission_date?: string;
    form_name?: string;
  },
): Record<string, string | undefined> {
  const ctx: Record<string, string | undefined> = {};

  // Flatten form data (string values only)
  for (const [k, v] of Object.entries(data)) {
    if (typeof v === "string") ctx[k] = v;
    else if (v != null) ctx[k] = String(v);
  }

  // Override with well-known static fields
  if (staticContext) {
    Object.entries(staticContext).forEach(([k, v]) => {
      if (v !== undefined) ctx[k] = v;
    });
  }

  return ctx;
}
